import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, errMsg, imgSrc } from "@/lib/api";

const EMPTY = { name: "", category: "smartphone", price: "", stock: "1", tagline: "", badge: "", image: "", specs: "" };
const field = "w-full rounded-2xl border border-fg/10 bg-[var(--ink)] px-4 py-3 text-fg outline-none placeholder:text-mist focus:border-[var(--volt)]/60";
const specsToText = (s) => Object.entries(s || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
const textToSpecs = (t) => Object.fromEntries(t.split("\n").map((l) => l.split(":")).filter((p) => p.length > 1 && p[0].trim())
  .map(([k, ...v]) => [k.trim(), v.join(":").trim()]));

export const ProductForm = ({ product, categories = [], onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!product?.id;

  useEffect(() => {
    if (product) setF(product.id ? { ...product, price: String(product.price), stock: String(product.stock), badge: product.badge || "", specs: specsToText(product.specs) } : { ...EMPTY, category: categories[0]?.id || "" });
  }, [product, categories]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await http.post("/admin/upload", body);
      setF((s) => ({ ...s, image: data.url }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const save = async (e) => {
    e.preventDefault();
    if (!f.image) { toast.error("Carica una foto del prodotto"); return; }
    setBusy(true);
    const payload = { name: f.name, category: f.category, price: Number(f.price), stock: Number(f.stock), tagline: f.tagline,
      badge: f.badge || null, image: f.image, specs: textToSpecs(f.specs) };
    try {
      if (isEdit) await http.put(`/admin/products/${product.id}`, payload);
      else await http.post("/admin/products", payload);
      toast.success(isEdit ? "Prodotto aggiornato" : "Prodotto aggiunto al negozio");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="admin-product-form" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-[2rem] border-fg/10 bg-[var(--ink-2)]">
        <DialogTitle className="font-display text-3xl">{isEdit ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
        <DialogDescription className="text-mist">Prezzo in euro, IVA inclusa.</DialogDescription>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="admin-product-image-button"
            className="relative flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-dashed border-fg/25 bg-[var(--ink)] sm:row-span-4">
            {f.image ? <img src={imgSrc(f.image)} alt="" className="h-full w-full object-cover" data-testid="admin-product-image-preview" /> : (
              <span className="flex flex-col items-center gap-2 text-sm text-mist"><ImagePlus className="h-8 w-8" /> Carica foto</span>
            )}
            {uploading && <span className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-6 w-6 animate-spin" /></span>}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="admin-product-image-input" />
          <input required minLength={2} value={f.name} onChange={set("name")} placeholder="Nome" data-testid="admin-product-name-input" className={field} />
          <select required value={f.category} onChange={set("category")} data-testid="admin-product-category-select" className={field}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input required type="number" min="0.01" step="0.01" value={f.price} onChange={set("price")} placeholder="Prezzo €" data-testid="admin-product-price-input" className={field} />
          <input required type="number" min="0" step="1" value={f.stock} onChange={set("stock")} placeholder="Pezzi disponibili" data-testid="admin-product-stock-input" className={field} />
          <input value={f.tagline} onChange={set("tagline")} maxLength={120} placeholder="Descrizione breve" data-testid="admin-product-tagline-input" className={`${field} sm:col-span-2`} />
          <input value={f.badge} onChange={set("badge")} maxLength={30} placeholder="Etichetta (es. Novità) — facoltativa" data-testid="admin-product-badge-input" className={`${field} sm:col-span-2`} />
          <textarea rows={4} value={f.specs} onChange={set("specs")} placeholder={"Caratteristiche, una per riga\nDisplay: 6,1\" OLED\nMemoria: 128 GB"} data-testid="admin-product-specs-input" className={`${field} sm:col-span-2`} />
          <button disabled={busy || uploading} data-testid="admin-product-save-button" className="flex items-center justify-center gap-2 rounded-full bg-[var(--volt)] py-4 font-semibold text-[var(--ink)] disabled:opacity-60 sm:col-span-2">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Salva
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
